"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const projects = [{
  number: "01",
  name: "The Hamlet Protocol",
  location: "ROYAL SHAKESPEARE THEATRE",
  year: "2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_hamlet_production.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_hamlet_production.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_hamlet_production.png-wvc-sizes",
  alt: "Jordan Rivers as Hamlet — high-contrast dramatic stage lighting with silver accents",
  aspect: "aspect-[4/5]",
  layout: "left"
}, {
  number: "02",
  name: "Neon Noir",
  location: "INDEPENDENT FEATURE FILM",
  year: "2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_neon_noir_still.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_neon_noir_still.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_neon_noir_still.webp-wvc-sizes",
  alt: "Jordan Rivers in a cinematic film still — moody shadows and sharp focus on eyes",
  aspect: "aspect-[4/5]",
  layout: "right-offset"
}, {
  number: "03",
  name: "The Showreel 2024",
  location: "CINEMATIC COMPILATION",
  year: "2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_showreel_thumbnail.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_showreel_thumbnail.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_showreel_thumbnail.png-wvc-sizes",
  alt: "Jordan Rivers showreel thumbnail — anamorphic widescreen ratio with film grain",
  aspect: "aspect-[16/9]",
  layout: "full"
}, {
  number: "04",
  name: "Studio Session IV",
  location: "LOS ANGELES, CA",
  year: "2023",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_headshot_silver.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_headshot_silver.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_headshot_silver.png-wvc-sizes",
  alt: "Jordan Rivers high-end headshot — monochrome palette with starlight silver highlights",
  aspect: "aspect-[4/5]",
  layout: "center-right"
}];
function ProjectCard({
  project,
  index
}: {
  project: (typeof projects)[number];
  index: number;
}) {
  const {
    ref
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: "0px 0px -80px 0px"
  });
  return <div ref={ref} data-index={index} className="group relative">
      <div className={`relative overflow-hidden ${project.aspect} bg-card rounded-md cursor-pointer border border-border`}>
        <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-background/0" />
        <div className="absolute inset-0 pointer-events-none opacity-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/noise.png')]" />
      </div>
      <div className="mt-6 flex items-start justify-between gap-6">
        <div className="flex items-start gap-6">
          <span className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground pt-1">
            {project.number}
          </span>
          <div>
            <h3 className="font-default italic text-lg text-foreground">
              {project.name}
            </h3>
            <p className="mt-1 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
              {project.location}
            </p>
          </div>
        </div>
        <span className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground pt-1">
          {project.year}
        </span>
      </div>
    </div>;
}
export default function TheShowreel() {
  const {
    ref: headerRef
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4952" id="the-showreel" className="bg-background text-foreground overflow-hidden ![padding:200px_64px_80px_64px]">
      <div className="max-w-[1440px] mx-auto">
        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8">
            <h2 className="font-default text-5xl md:text-6xl lg:text-[80px] leading-[1.0] tracking-tighter text-foreground uppercase">
              Performances <span className="text-foreground/50">defined</span>
              <br />
              by intensity.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:pt-4">
            <div className="max-w-[280px]">
              <p className="text-base leading-relaxed text-muted-foreground font-sans font-light">
                A curated selection of dramatic works — from the visceral energy of the stage to the intimate precision of the lens. Each frame captures a moment of truth in the pursuit of the craft.
              </p>
              <Link to="/VisualArchivePage" className="group inline-flex items-center gap-2 mt-8 font-mono text-[11px] tracking-[0.2em] uppercase text-foreground">
                <span className="relative">
                  View full archive
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-foreground origin-left scale-x-100 transition-transform duration-500 group-hover:scale-x-0" />
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-foreground origin-right scale-x-0 transition-transform duration-500 delay-150 group-hover:scale-x-100" />
                </span>
                <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-24 md:mt-32 lg:mt-[120px] space-y-24 md:space-y-32">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-7">
              <ProjectCard project={projects[0]} index={0} />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 lg:-mt-12">
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 lg:mt-20">
              <ProjectCard project={projects[1]} index={1} />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 lg:pt-16">
            <div className="col-span-12">
              <ProjectCard project={projects[2]} index={2} />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-6 lg:col-start-5">
              <ProjectCard project={projects[3]} index={3} />
            </div>
          </div>
        </div>

        <div className="mt-32 md:mt-40">
          <Separator className="bg-border" />
          <div className="flex justify-center pt-12">
            <Button asChild variant="link" className="group font-mono text-[11px] tracking-[0.25em] uppercase text-foreground hover:no-underline px-0 rounded-md">
              <Link to="/VisualArchivePage" className="inline-flex items-center gap-3">
                <span className="relative">
                  Explore the complete reel
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-foreground origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100" />
                </span>
                <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}